import { EARTH_RADIUS_KILOMETERS, MS_PER_HOUR, MS_PER_MINUTE } from '../constants';
import { Gait } from '../enums';
import { RidePath, RideWithoutIds, WayPoint } from '../models';
import { GaitNumbers } from '../types';

export function degToRad(degrees: number): number {
    // tslint:disable-next-line:no-magic-numbers
    return degrees * (Math.PI / 180);
}

/*
    Calculate distance between two gps points using the Haversine formula
*/
export function wayPointKilometers(source: WayPoint, destination: WayPoint): number {
    if (!destination || !source) {
        return 0;
    }
    
    const rlat1 = degToRad(source.latitude); // Convert degrees to radians
    const rlat2 = degToRad(destination.latitude); // Convert degrees to radians
    const difflat = rlat2 - rlat1; // Radian difference (latitudes)
    // tslint:disable-next-line:no-magic-numbers
    const difflon = (destination.longitude - source.longitude) * (Math.PI / 180); // Radian difference (longitudes)
    // tslint:disable-next-line:no-magic-numbers
    const distance =
        2 *
        EARTH_RADIUS_KILOMETERS *
        Math.asin(
            Math.sqrt(
                Math.sin(difflat / 2) *
                    // tslint:disable-next-line:no-magic-numbers
                    Math.sin(difflat / 2) +
                    Math.cos(rlat1) *
                        Math.cos(rlat2) *
                        Math.sin(difflon / 2) *
                        Math.sin(difflon / 2)
            )
        );
    // Ensure distance is always positive and valid
    const result = Math.abs(distance);
    return isNaN(result) || !isFinite(result) ? 0 : result;
}

export function getKphGait(
    kmPerHour: number,
    horseGaitsKph: GaitNumbers,
    preventErrorOnGaitNotFound = false
): Gait {
    for (let _g = Gait.members.length - 1; _g >= 0; --_g) {
        const g = Gait.members[_g];
        const gaitLowerBound = horseGaitsKph[g];
        if (kmPerHour >= gaitLowerBound) {
            return g;
        }
    }
    if (!preventErrorOnGaitNotFound) {
        throw new Error(`Gait not found for kph ${kmPerHour}`);
    }
    return null;
}

export function wayPointPairStats(
    a: WayPoint,
    b: WayPoint,
    horseGaitsKph: GaitNumbers,
    isViewRide = true
): { kilometers: number; millisec: number; gait: Gait } {
    if (a == undefined || b == undefined) {
        return null;
    }
    const millisec = new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    
    // Ensure we don't have negative time differences or invalid timestamps
    if (millisec <= 0 || isNaN(millisec)) {
        return { kilometers: 0, millisec: 0, gait: Gait.Walk };
    }
    
    const hours = millisec / MS_PER_HOUR;
    const kilometers = wayPointKilometers(a, b);
    
    // Ensure we don't divide by zero or have negative values
    if (hours <= 0 || kilometers < 0) {
        return { kilometers: Math.max(0, kilometers), millisec: Math.max(0, millisec), gait: Gait.Walk };
    }
    
    const kmPerHour = kilometers / hours;
    
    // Ensure speed is not negative or invalid
    const validKmPerHour = isNaN(kmPerHour) || !isFinite(kmPerHour) || kmPerHour < 0 ? 0 : kmPerHour;
    
    const gait = getKphGait(validKmPerHour, horseGaitsKph, isViewRide);
    return { kilometers: Math.max(0, kilometers), millisec: Math.max(0, millisec), gait };
}

export function getRideKilometers(ride: RideWithoutIds, ridePath: WayPoint[]): number {
    if (ride.distanceKilometers > 0) {
        return ride.distanceKilometers;
    }

    if (ridePath.length <= 1) {
        return ride.distanceKilometers;
    }

    let kilometers = 0;
    let count = 0;
    for (const p of ridePath) {
        // for (let i = 0; i < p.wayPoints.length - 1; ++i) {
        kilometers += wayPointKilometers(ridePath[count], ridePath[count + 1]);
        // }
        count++;
    }
    return kilometers;
}

/*
    Sum distance per gait respecting gaps between paths for ride pauses
*/
export function getRideKilometersPerGait(
    ridePath: WayPoint[],
    pausedWaypoints: WayPoint[],
    horseGaitsKph: GaitNumbers
): GaitNumbers {
    const totals = Gait.gaitNumbersZeroed();
    for (let _w = 0; _w < ridePath.length - 1; ++_w) {
        if (!pausedWaypoints.some(p => p.timestamp === ridePath[_w].timestamp || `${p.timestamp}` === `${ridePath[_w].timestamp}`)) { //if the waypoint was not a paused waypoint
            const stats = wayPointPairStats(ridePath[_w], ridePath[_w + 1], horseGaitsKph);
            totals[stats.gait] += stats.kilometers;
        }
    }
    return totals;
}

/*
    Sum distance per gait respecting gaps between paths for ride pauses
*/
export function getRideKilometersPerGaitByRidePath(
    ridePath: RidePath[],
    pausedWaypoints: WayPoint[],
    horseGaitsKph: GaitNumbers
): GaitNumbers {
    const totals = Gait.gaitNumbersZeroed();
    for (const path of ridePath) {
        for (let _w = 0; _w < path.wayPoints.length - 1; ++_w) {
            if (!pausedWaypoints.some(p => p.timestamp === path[_w].timestamp || `${p.timestamp}` === `${path[_w].timestamp}`)) { //if the waypoint was not a paused waypoint
                const stats = wayPointPairStats(path.wayPoints[_w], path.wayPoints[_w + 1], horseGaitsKph);
                totals[stats.gait] += stats.kilometers;
            }
        }
    }
    return totals;
}

/*
    Sum time per gait respecting gaps between paths for ride pauses
*/
export function getRideMinutesPerGait(
    ridePath: WayPoint[],
    pausedWaypoints: WayPoint[],
    horseGaitsKph: GaitNumbers
): GaitNumbers {
    const totals = Gait.gaitNumbersZeroed();
    const testTotals = Gait.gaitNumbersZeroed();
    for (let _w = 0; _w < ridePath.length - 1; ++_w) {
        if (!pausedWaypoints.some(p => p.timestamp === ridePath[_w].timestamp || `${p.timestamp}` === `${ridePath[_w].timestamp}`)) { //if the waypoint was not a paused waypoint
            const stats = wayPointPairStats(ridePath[_w], ridePath[_w + 1], horseGaitsKph);
            totals[stats.gait] += stats.millisec / MS_PER_MINUTE;
        } 
    }
    for (let _w = 0; _w < ridePath.length - 1; ++_w) {
         //if the waypoint was not a paused waypoint
        const testStats = wayPointPairStats(ridePath[_w], ridePath[_w + 1], horseGaitsKph);
        testTotals[testStats.gait] += testStats.millisec / MS_PER_MINUTE;
        
    }
    return totals;
}

/*
    Sum total ride time respecting gaps between paths for ride pauses
*/
export function calculateRideTime(ride: RideWithoutIds): number {
    if (ride.endDateTime && ride.paths) {
        // pull the endtime of the first recorded path entry
        // use this value instead of the ride end time to account for pauses and when the end ride modal is open (timer continues to count), more accurate
        const endDate = ride.paths[0].endDateTime;
        const firstPathTime = new Date(endDate).getTime() - new Date(ride.startDateTime).getTime();
        // if there is only one path, return that time
        if (ride.paths.length === 1) {
            return firstPathTime;
        } else if (ride.paths.length > 1) {
            // if there are multiple paths, we need to caclulate the time between the start and end times for each subesequent path to avoid including pauses
            // remove the already accounted for first path, can't modify original so need to make a copy
            const pathArrayCopy = [...ride.paths];
            pathArrayCopy.shift();
            const pathTotals = pathArrayCopy.reduce((total, path) => {
                const startTime = new Date(path.startDateTime);
                const endTime = new Date(path.endDateTime);
                const pathDuration = endTime.getTime() - startTime.getTime();
                // then add those values to the first total
                return total + pathDuration;
            }, firstPathTime);
            return pathTotals;
        }
    }

    // used for when the ride is still in progress/not yet officially ended
    const initialDuration = 0;
    return ride.paths.reduce((total, path) => {
        const endDate = path.endDateTime ? new Date(path.endDateTime) : new Date();
        const pathDuration = endDate.getTime() - new Date(path.startDateTime).getTime();

        return total + pathDuration;
    }, initialDuration);
}
